import { DOCUMENT, isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink, Scroll } from '@angular/router';
import { filter } from 'rxjs';

import { LEGAL_DOCS } from './legal-content';
import { LegalDocSlug, LegalDocument } from './legal.model';

/**
 * Página de presentación reutilizable (data-driven) para los 4 documentos
 * legales. Selecciona el documento a partir de `route.data.legalDoc` y lo pinta
 * desde `LEGAL_DOCS`. El SEO ya lo aplicó `seoResolver` en la ruta, así que el
 * componente no toca metadatos (mismo patrón que `AboutPageComponent`).
 *
 * **Ancla del fragmento (P3 de B11, D30)**: `/privacidad#directorio-de-jugadores-y-solicitudes-para-jugar`
 * baja a esa sección. Lo resuelve la página, no la app (sin `anchorScrolling` global en
 * `app.config.ts`). Se engancha al evento `Scroll` del router y no a `afterNextRender`:
 * con `scrollPositionRestoration: 'top'` el router sube al principio tras un
 * `setTimeout`/`requestAnimationFrame` posterior al render, y pisaría el scroll. Ante el
 * evento `Scroll`, el ancla se aplica en una microtarea, detrás de la subida del router sea
 * cual sea el orden de suscripción. Solo en el navegador; en el SSR y el prerender, nada.
 */
@Component({
  selector: 'app-legal-page',
  imports: [RouterLink, NgTemplateOutlet],
  templateUrl: './legal-page.component.html',
  styleUrl: './legal-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class LegalPageComponent {
  private readonly route = inject(ActivatedRoute);

  private readonly slug = this.route.snapshot.data['legalDoc'] as LegalDocSlug;

  protected readonly doc: LegalDocument = LEGAL_DOCS[this.slug];

  constructor() {
    if (!isPlatformBrowser(inject(PLATFORM_ID))) return;
    const document = inject(DOCUMENT);
    const router = inject(Router);
    let destroyed = false;
    inject(DestroyRef).onDestroy(() => (destroyed = true));
    router
      .events.pipe(
        filter((event): event is Scroll => event instanceof Scroll && !!event.anchor),
        takeUntilDestroyed(),
      )
      // En una microtarea: en la carga inicial esta página se suscribe ANTES que el
      // `RouterScroller` (que arranca en el bootstrap, tras la navegación inicial), así
      // que, dentro del mismo `Scroll`, su subida al principio llegaría después y pisaría
      // el ancla. La microtarea corre cuando ya han pasado todos los suscriptores.
      // Y, si las fuentes aún cargan, otra vez cuando terminan: su cambio reflujaba el texto de
      // encima y dejaba la sección desplazada (verificado en el navegador).
      .subscribe((event) =>
        queueMicrotask(() => {
          const scroll = () => document.getElementById(event.anchor!)?.scrollIntoView({ block: 'start' });
          scroll();
          // El re-scroll de las fuentes no sobrevive a la página (Astra J3 H5): si para entonces se
          // navegó a otra, o el fragmento ya no es este, no se mueve nada.
          const stillHere = () => !destroyed && router.parseUrl(router.url).fragment === event.anchor;
          if (document.fonts && document.fonts.status !== 'loaded') {
            void document.fonts.ready.then(() => {
              if (stillHere()) scroll();
            });
          }
        }),
      );
  }
}
